import { useState } from "react";
import { Plus, Pencil, Trash2, TriangleAlert } from "lucide-react";
import { useAllergies, useDeleteAllergy, type AllergySummary } from "../../lib/api/useAllergies";
import { useMedications } from "../../lib/api/useMedications";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { AllergyDialog } from "./AllergyDialog";
import { AllergyPlanPanel } from "./AllergyPlanPanel";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { SeverityBadge } from "../ui/SeverityBadge";
import { SectionCard } from "../ui/SectionCard";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { EmptyState } from "../ui/EmptyState";
import { SECTION_ACCENT } from "../../lib/accents";
import { INLINE_STATUS, SEVERITY_ROW } from "../../lib/surfaces";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";

export function AllergyList({ childId }: { childId: string }) {
  const { data: allergies, isLoading, error } = useAllergies(childId);
  // Resolves rescueMedicationId -> name for the plan panel. The owner always has
  // the medications list, so unlike the caregiver view this is never gated.
  const { data: medications } = useMedications(childId);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AllergySummary | undefined>();
  const [deleting, setDeleting] = useState<AllergySummary | undefined>();
  const deleteMutation = useDeleteAllergy(childId);
  const { publish } = useToast();

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.id);
      publish({ variant: "success", title: "Allergy deleted successfully" });
      setDeleting(undefined);
    } catch (err) {
      publish({
        variant: "error",
        title: "Failed to delete allergy",
        description: err instanceof ApiError ? err.message : undefined,
      });
      setDeleting(undefined);
    }
  };

  return (
    <SectionCard
      icon={TriangleAlert}
      title="Allergies & Restrictions"
      accent={SECTION_ACCENT.allergies}
      actions={
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Add Allergy
        </Button>
      }
    >
      {isLoading && (
        <div role="status" className={INLINE_STATUS}>
          <Spinner size="sm" />
          Loading…
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error.message}</p>}

      {!isLoading && !error && (allergies?.length ?? 0) === 0 && (
        <EmptyState
          size="sm"
          accent="rose"
          icon={TriangleAlert}
          title="No allergies recorded"
          description="Add any allergies or dietary restrictions so caregivers know exactly what to avoid."
        />
      )}

      {!isLoading && !error && allergies && allergies.length > 0 && (
        <ul className="space-y-3">
          {allergies.map((allergy) => (
            <li
              key={allergy.id}
              className={SEVERITY_ROW[allergy.severity]}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-neutral-900">
                      {allergy.name}
                    </h3>
                    <SeverityBadge severity={allergy.severity} />
                  </div>
                  {allergy.description && (
                    <p className="mt-1 text-sm text-neutral-700">{allergy.description}</p>
                  )}
                  <AllergyPlanPanel
                    severity={allergy.severity}
                    reactionSymptoms={allergy.reactionSymptoms}
                    treatment={allergy.treatment}
                    epinephrineLocation={allergy.epinephrineLocation}
                    rescueMedicationName={
                      allergy.rescueMedicationId
                        ? (medications?.find((m) => m.id === allergy.rescueMedicationId)?.name ??
                          null)
                        : null
                    }
                  />
                  {allergy.emergencyAction && (
                    <div className="mt-2 flex items-start gap-1.5 rounded-xl bg-white/70 p-2.5">
                      <TriangleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" aria-hidden />
                      <div className="text-xs">
                        <span className="font-semibold text-red-800">Emergency Action: </span>
                        <span className="text-neutral-800">{allergy.emergencyAction}</span>
                      </div>
                    </div>
                  )}
                  {allergy.notes && (
                    <p className="mt-2 text-xs italic text-neutral-600">{allergy.notes}</p>
                  )}
                </div>
                <div className="flex flex-shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => setEditing(allergy)}
                    aria-label={`Edit ${allergy.name}`}
                    className={cn(
                      "rounded-full p-1.5 text-neutral-600 transition hover:bg-white hover:text-neutral-900",
                      FOCUS_RING,
                    )}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(allergy)}
                    aria-label={`Delete ${allergy.name}`}
                    className={cn(
                      "rounded-full p-1.5 text-neutral-600 transition hover:bg-white hover:text-red-700",
                      FOCUS_RING,
                    )}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <AllergyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        childId={childId}
      />

      <AllergyDialog
        open={Boolean(editing)}
        onOpenChange={(open) => !open && setEditing(undefined)}
        childId={childId}
        allergy={editing}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(undefined)}
        title="Delete allergy"
        description={`Are you sure you want to delete this allergy${deleting ? ` "${deleting.name}"` : ""}?`}
        confirmLabel="Delete"
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={() => {
          void handleDelete();
        }}
      />
    </SectionCard>
  );
}
